class ProductPage {
  constructor() {
    this.productEl = document.querySelector(".product");
    if (!this.productEl) return;

    this.productId = this.productEl.dataset.productId;
    this.productData = this.getProductData();

    this.ThumbsSwiper = this.initThumbsSwiper();
    this.Swiper = this.initHeroSwiper();

    this.initQuantitySelector();
    this.initTabs();
    this.initDescriptionToggle();
    this.initReviewsScroll();

    if (!this.productData) return;

    this.initVariantLogic();
    this.syncFromURL();
  }

  /* -----------------------------------------------------------------------
     THUMBNAIL SWIPER (vertical, left of hero — desktop only via CSS)
  ----------------------------------------------------------------------- */
  initThumbsSwiper() {
    const swiperEl = document.getElementById("pdp-thumbs-swiper");
    if (!swiperEl || typeof Swiper === "undefined") return null;

    return new Swiper(swiperEl, {
      direction: "vertical",
      slidesPerView: "auto",
      spaceBetween: 12,
      watchSlidesProgress: true,
      navigation: {
        prevEl: "#pdp-thumbs-prev",
        nextEl: "#pdp-thumbs-next",
      },
      breakpoints: {
        0: { direction: "horizontal" },
        1024: { direction: "vertical" },
      },
    });
  }

  /* -----------------------------------------------------------------------
     HERO SWIPER (standard PDP — left column of 2-column layout)
  ----------------------------------------------------------------------- */
  initHeroSwiper() {
    const swiperEl = document.getElementById("pdp-hero-swiper");
    const paginationEl = document.getElementById("pdp-pagination");
    if (!swiperEl || typeof Swiper === "undefined") return null;

    const options = {
      direction: "horizontal",
      loop: false,
      slidesPerView: 1,
      speed: 300,
      pagination: {
        el: paginationEl,
        clickable: true,
        bulletClass: "product-hero__dot",
        bulletActiveClass: "product-hero__dot--active",
      },
      on: {
        slideChange(swiper) {
          swiper.slides.forEach((slide, i) => {
            if (i === swiper.activeIndex) return;
            slide.querySelectorAll("video").forEach((video) => {
              if (!video.paused && video.controls) video.pause();
            });
          });
        },
      },
    };

    if (this.ThumbsSwiper) {
      options.thumbs = { swiper: this.ThumbsSwiper };
    }

    return new Swiper(swiperEl, options);
  }

  /* -----------------------------------------------------------------------
     INFO TABS (desktop) / ACCORDION (mobile)
     One set of markup, two behaviours:
     — .pdp-tabs__nav-btn buttons act as tabs (visible ≥1024px)
     — .pdp-tabs__accordion-trigger buttons act as an accordion (mobile)
  ----------------------------------------------------------------------- */
  initTabs() {
    const wrapper = document.querySelector("[data-pdp-tabs]");
    if (!wrapper) return;

    const navBtns = [...wrapper.querySelectorAll("[data-pdp-tab-btn]")];
    const items = [...wrapper.querySelectorAll("[data-pdp-tab-item]")];

    const activate = (index, exclusive) => {
      items.forEach((item, i) => {
        const isActive = i === index;
        const trigger = item.querySelector("[data-pdp-tab-trigger]");
        if (exclusive || isActive) {
          item.classList.toggle("is-active", isActive);
          if (trigger) trigger.setAttribute("aria-expanded", String(isActive));
        }
      });
      navBtns.forEach((btn, i) => {
        btn.classList.toggle("is-active", i === index);
        btn.setAttribute("aria-selected", String(i === index));
      });
    };

    navBtns.forEach((btn, index) => {
      btn.addEventListener("click", () => activate(index, true));
    });

    // Mobile accordion — non-exclusive toggle
    items.forEach((item, index) => {
      const trigger = item.querySelector("[data-pdp-tab-trigger]");
      if (!trigger) return;
      trigger.addEventListener("click", () => {
        const isOpen = item.classList.contains("is-active");
        item.classList.toggle("is-active", !isOpen);
        trigger.setAttribute("aria-expanded", String(!isOpen));
        if (!isOpen) {
          navBtns.forEach((btn, i) => {
            btn.classList.toggle("is-active", i === index);
            btn.setAttribute("aria-selected", String(i === index));
          });
        }
      });
    });
  }

  /* -----------------------------------------------------------------------
     DESCRIPTION "MORE" TOGGLE
  ----------------------------------------------------------------------- */
  initDescriptionToggle() {
    const wrapper = document.querySelector("[data-pdp-description]");
    if (!wrapper) return;
    const text = wrapper.querySelector("[data-pdp-description-text]");
    const toggle = wrapper.querySelector("[data-pdp-description-toggle]");
    if (!text || !toggle) return;

    // Only clamp + show the toggle for really long descriptions.
    // While clamped, clientHeight ≈ 2 lines — estimate a line height from it
    // and only keep the More/Less behaviour when the full text exceeds
    // LONG_DESCRIPTION_LINES lines. Otherwise show the text in full, no toggle.
    const LONG_DESCRIPTION_LINES = 6;
    const lineHeight = text.clientHeight / 2;
    const isReallyLong =
      lineHeight > 0 &&
      text.scrollHeight > lineHeight * LONG_DESCRIPTION_LINES;

    if (!isReallyLong) {
      text.classList.remove("is-clamped");
      toggle.style.display = "none";
      return;
    }

    const moreLabel = toggle.textContent.trim();

    toggle.addEventListener("click", () => {
      const expanded = toggle.getAttribute("aria-expanded") === "true";
      text.classList.toggle("is-clamped", expanded);
      toggle.setAttribute("aria-expanded", String(!expanded));
      toggle.textContent = expanded ? moreLabel : "Less";
    });
  }

  /* -----------------------------------------------------------------------
     REVIEWS — smooth-scroll to the review widget (Judge.me)
  ----------------------------------------------------------------------- */
  initReviewsScroll() {
    const trigger = document.querySelector("[data-scroll-to-reviews]");
    if (!trigger) return;

    trigger.addEventListener("click", () => {
      const widget = document.querySelector(".jdgm-rev-widg, #judgeme_product_reviews");
      if (widget) widget.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  /* -----------------------------------------------------------------------
     QUANTITY SELECTOR (+ / −)
  ----------------------------------------------------------------------- */
  initQuantitySelector() {
    const wrapper = this.productEl.querySelector("[data-quantity-selector]");
    if (!wrapper) return;
    const input = wrapper.querySelector("[data-quantity-input]");
    if (!input) return;

    const min = parseInt(input.min, 10) || 1;

    wrapper.querySelectorAll("[data-qty-action]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const current = parseInt(input.value, 10) || min;
        const next =
          btn.dataset.qtyAction === "increase"
            ? current + 1
            : Math.max(min, current - 1);
        input.value = next;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });

    input.addEventListener("change", () => {
      const val = parseInt(input.value, 10);
      if (isNaN(val) || val < min) input.value = min;
      this.updateAtcPrice();
    });
  }

  /* -----------------------------------------------------------------------
     LIVE ATC BUTTON PRICE — "Add to Cart | £16.99" (variant price × qty)
  ----------------------------------------------------------------------- */
  updateAtcPrice() {
    const priceEl = document.querySelector("[data-atc-price]");
    if (!priceEl || !this.productData) return;

    const variantId =
      this.currentVariantId || document.getElementById("product-select")?.value;
    const formatted = this.formattedPrices?.[variantId]?.price;
    if (!formatted) return;

    const input = this.productEl.querySelector("[data-quantity-input]");
    const qty = Math.max(1, parseInt(input?.value, 10) || 1);

    if (qty === 1) {
      priceEl.innerHTML = formatted;
      return;
    }

    const variant = this.productData.variants.find((v) => v.id == variantId);
    if (!variant || typeof formatMoney !== "function") return;
    priceEl.innerHTML = formatMoney(formatted, variant.price, qty);
  }

  /* GET PRODUCT DATA */
  getProductData() {
    const jsonEl = document.getElementById(`ProductData-${this.productId}`);
    if (!jsonEl) return null;

    try {
      const data = JSON.parse(jsonEl.textContent);
      this.variantImages = data.variantImages || {};
      this.formattedPrices = data.formattedPrices || {};
      this.inventoryPolicies = data.inventoryPolicies || {};
      return data.product;
    } catch (e) {
      console.error("Product JSON parse error:", e);
      return null;
    }
  }

  /**
   * Returns true if a variant is sellable — either it has available inventory,
   * or its inventory_policy is "continue" (sell when out of stock).
   * Shopify's product | json can return available: false even when inventory_policy
   * is "continue", so we check both.
   */
  isVariantSellable(variant) {
    if (!variant) return false;
    if (this.inventoryPolicies[variant.id] === "continue") return true;
    return !!variant.available;
  }

  /* VARIANTS */
  initVariantLogic() {
    this.pillGroups = document.querySelectorAll(".product__variant-pills");
    this.selectGroups = document.querySelectorAll(".product__variant-select");
    this.variantInput = document.getElementById("product-select");
    this.priceSale = document.getElementById("product__price-sale");
    this.priceOg = document.getElementById("product__price-og");
    this.addBtn = document.getElementById("product-btn-avail");
    this.soldBtn = document.getElementById("product-btn-sold");

    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get("variant");

    let initialVariant =
      (variantId && this.productData.variants.find((v) => v.id == variantId)) ||
      this.productData.variants.find((v) => this.isVariantSellable(v)) ||
      this.productData.variants[0];

    if (!initialVariant) return;

    this.setSelectionFromVariant(initialVariant);
    this.updateVariantUI(initialVariant);

    this.pillGroups.forEach((group) => {
      group.addEventListener("click", (e) => {
        const btn = e.target.closest("button");
        if (!btn) return;
        group
          .querySelectorAll("button")
          .forEach((b) => b.classList.remove("active"));
        btn.classList.add("active");
        this.updateFromSelected();
      });
    });

    this.selectGroups.forEach((select) => {
      select.addEventListener("change", () => this.updateFromSelected());
    });
  }

  /* Returns the currently selected value for an option position (1-based index from options array), or null. */
  getSelectedValue(index) {
    const position = index + 1;
    const pillGroup = document.querySelector(
      `.product__variant-pills[data-option-position="${position}"]`,
    );
    if (pillGroup) {
      return pillGroup.querySelector(".active")?.dataset.value || null;
    }
    const selectGroup = document.querySelector(
      `.product__variant-select[data-option-position="${position}"]`,
    );
    if (selectGroup) {
      return selectGroup.value || null;
    }
    return null;
  }

  updateFromSelected() {
    const selectedOptions = this.productData.options.map((_, i) =>
      this.getSelectedValue(i),
    );

    if (selectedOptions.some((opt) => !opt)) {
      this.showSoldOut();
      return;
    }

    const matchedVariant = this.productData.variants.find((variant) =>
      selectedOptions.every((opt, i) => variant[`option${i + 1}`] === opt),
    );

    if (matchedVariant) {
      this.updateVariantUI(matchedVariant);
    }
  }

  updateVariantUI(variant) {
    this.currentVariantId = variant.id;

    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    if (this.isVariantSellable(variant)) {
      this.showAvailable();
    } else {
      this.showSoldOut();
    }

    if (this.priceOg && this.formattedPrices[variant.id]) {
      this.priceOg.innerHTML = this.formattedPrices[variant.id].price;
    }

    if (this.priceSale && this.formattedPrices[variant.id]) {
      if (variant.compare_at_price > variant.price) {
        this.priceSale.style.display = "block";
        this.priceSale.innerHTML = this.formattedPrices[variant.id].compare;
      } else {
        this.priceSale.style.display = "none";
      }
    }

    this.productData.options.forEach((_, index) => {
      const label = document.querySelector(
        `.active-option-label[data-option-position="${index + 1}"]`,
      );

      if (label) {
        label.textContent = variant[`option${index + 1}`];
      }
    });

    this.updateURL(variant.id);
    this.updateVariantAvailability();
    this.updateGallery(variant);
    this.updateAtcPrice();
  }

  showAvailable() {
    if (this.addBtn) this.addBtn.style.display = "block";
    if (this.soldBtn) this.soldBtn.style.display = "none";
  }

  showSoldOut() {
    if (this.addBtn) this.addBtn.style.display = "none";
    if (this.soldBtn) this.soldBtn.style.display = "block";
  }

  updateVariantAvailability() {
    const selectedOptions = this.productData.options.map((_, i) =>
      this.getSelectedValue(i),
    );

    const isValueSellable = (index, value) => {
      const testOptions = [...selectedOptions];
      testOptions[index] = value;
      const matchingVariant = this.productData.variants.find((variant) =>
        testOptions.every((opt, i) => !opt || variant[`option${i + 1}`] === opt),
      );
      return this.isVariantSellable(matchingVariant);
    };

    this.productData.options.forEach((_, index) => {
      const position = index + 1;

      const pillGroup = document.querySelector(
        `.product__variant-pills[data-option-position="${position}"]`,
      );
      if (pillGroup) {
        pillGroup.querySelectorAll("button").forEach((btn) => {
          btn.classList.toggle(
            "is-out-of-stock",
            !isValueSellable(index, btn.dataset.value),
          );
        });
      }

      const selectGroup = document.querySelector(
        `.product__variant-select[data-option-position="${position}"]`,
      );
      if (selectGroup) {
        selectGroup.querySelectorAll("option").forEach((opt) => {
          opt.disabled = !isValueSellable(index, opt.value);
        });
      }
    });
  }

  updateGallery(variant) {
    if (!this.Swiper) return;

    // Use the variant's featured_image position (1-based) to scroll the
    // carousel to the matching slide. Falls back gracefully if not set.
    const pos = variant?.featured_image?.position;
    if (!pos) return;

    this.Swiper.slideTo(pos - 1);
  }

  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    window.history.replaceState({}, "", url);
  }

  setSelectionFromVariant(variant) {
    this.productData.options.forEach((_, index) => {
      const position = index + 1;
      const value = variant[`option${position}`];

      const pillGroup = document.querySelector(
        `.product__variant-pills[data-option-position="${position}"]`,
      );
      if (pillGroup) {
        pillGroup.querySelectorAll("button").forEach((btn) => {
          btn.classList.toggle("active", btn.dataset.value === value);
        });
      }

      const selectGroup = document.querySelector(
        `.product__variant-select[data-option-position="${position}"]`,
      );
      if (selectGroup) {
        selectGroup.value = value;
      }
    });
  }

  syncFromURL() {
    const variantId = new URLSearchParams(window.location.search).get(
      "variant",
    );
    if (!variantId) return;

    const variant = this.productData.variants.find((v) => v.id == variantId);
    if (!variant) return;

    this.updateVariantUI(variant);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new ProductPage();
});
